import { useState, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface FaqModalItemProps {
  question: string;
  answer: ReactNode;
  /** Extra classes for the trigger button (kept for visual parity with the previous AccordionTrigger). */
  triggerClassName?: string;
  /** Extra classes for the answer body inside the modal (kept for text styling parity). */
  answerClassName?: string;
  /** Optional wrapper class around the trigger row (border/padding parity with AccordionItem). */
  itemClassName?: string;
}

/**
 * FAQ row that opens the answer in a centered modal instead of expanding inline.
 * Visually mirrors the previous Accordion trigger (question + chevron) so pages
 * keep their existing look; only the interaction changes.
 */
export function FaqModalItem({
  question,
  answer,
  triggerClassName,
  answerClassName,
  itemClassName,
}: FaqModalItemProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("border-b last:border-0", itemClassName)}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        className={cn(
          "flex w-full flex-1 items-center justify-between py-4 text-sm font-medium cursor-pointer transition-all hover:underline text-left",
          triggerClassName,
        )}
      >
        <span className="flex-1">{question}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
      </button>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <DialogPrimitive.Content
            className="fixed left-1/2 top-1/2 z-50 grid w-[calc(100%-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-2xl border bg-background p-5 md:p-6 shadow-xl max-h-[85vh] overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
            <DialogPrimitive.Title
              className={cn("pr-10 text-left leading-snug font-semibold", triggerClassName)}
            >
              {question}
            </DialogPrimitive.Title>
            <div
              className={cn(
                "text-muted-foreground leading-relaxed whitespace-pre-line text-left text-sm md:text-base",
                answerClassName,
              )}
            >
              {answer}
            </div>
            <DialogPrimitive.Close
              aria-label="Close"
              className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground/70 hover:bg-muted/80 hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
}
